import React, { useState, useEffect } from "react"
import { Container, Row, Col, Table, Modal, ModalHeader, ModalBody } from "reactstrap"
import { Tabs, Tab } from "react-bootstrap"
import "./CustomInsight.css"
import { useNavigate } from "react-router-dom"
import SkeletonParagraph from "../../additional_components/common/SkeletonParagraph"
import OptimizedImage from "../../components/Common/OptimizedImage"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, applySEODate } from "../../utils/seoUtils"
import { Star, ChevronDown, ChevronUp, X } from "lucide-react"
import axios from "axios"
import { api} from "../../utils/variables"
import { triggerToast, configureToastr } from "../../utils/toast"
import ReactApexChart from "react-apexcharts"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

export default function TopGenres2025() {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const [genres, setGenres] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedGenre, setExpandedGenre] = useState(null)
  const [genreData, setGenreData] = useState({})
  const [loadingGenreData, setLoadingGenreData] = useState({})
  const [selectedGenres, setSelectedGenres] = useState([])
  const [showComparisonModal, setShowComparisonModal] = useState(false)
  const [comparisonData, setComparisonData] = useState(null)
  const [loadingComparison, setLoadingComparison] = useState(false)
  
  // Date range state
  const [fromDate, setFromDate] = useState(() => {
    try {
      const stored = sessionStorage.getItem('topGenres2025_fromDate')
      if (stored) return stored
    } catch (e) {}
    // Default: January 1, 2025
    return '2025-01-01'
  })
  
  const [toDate, setToDate] = useState(() => {
    try {
      const stored = sessionStorage.getItem('topGenres2025_toDate')
      if (stored) return stored
    } catch (e) {}
    // Default: December 31, 2025
    return '2025-12-31'
  })

  // Initialize toast on mount
  useEffect(() => {
    configureToastr()
  }, [])

  // Fetch top genres from API
  useEffect(() => {
    fetchTopGenres()
  }, [])

  // Format date from YYYY-MM-DD to YYYY-MM
  const formatDateForAPI = (dateString) => {
    if (!dateString) return null
    return dateString.substring(0, 7) // Extract YYYY-MM from YYYY-MM-DD
  }

  const fetchTopGenres = async () => {
    try {
      setLoading(true)
      
      // Format dates for API (YYYY-MM:YYYY-MM format)
      const fromMonth = formatDateForAPI(fromDate)
      const toMonth = formatDateForAPI(toDate)
      
      // Build query params
      const params = {}
      if (fromMonth && toMonth) {
        params.range = `${fromMonth}:${toMonth}`
      }
      
      const response = await axios.get(`${api}/tracker/stats/top_genres`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      })
      
      if (response.data && response.data.status === 200 && response.data.data) {
        setGenres(response.data.data)
        trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_VIEWED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_GENRES })
      }
    } catch (error) {
      console.error("Error fetching top genres:", error)
      setGenres([])
    } finally {
      setLoading(false)
    }
  }

  const fetchGenreInfo = async (genreName) => {
    try {
      setLoadingGenreData(prev => ({ ...prev, [genreName]: true }))
      
      // Format dates for API (YYYY-MM:YYYY-MM format)
      const fromMonth = formatDateForAPI(fromDate)
      const toMonth = formatDateForAPI(toDate)
      
      // Build query params
      const params = {
        genres: genreName
      }
      if (fromMonth && toMonth) {
        params.range = `${fromMonth}:${toMonth}`
      }
      
      const response = await axios.get(`${api}/tracker/stats/genre_info`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      })
      
      if (response.data && response.data.status === 200 && response.data.data) {
        setGenreData(prev => ({ ...prev, [genreName]: response.data.data }))
        setExpandedGenre(genreName)
      }
    } catch (error) {
      console.error("Error fetching genre info:", error)
      triggerToast('Failed to load genre data. Please try again.', 'error', 'Error')
    } finally {
      setLoadingGenreData(prev => ({ ...prev, [genreName]: false }))
    }
  }

  const handleGenreClick = (genreName) => {
    if (expandedGenre === genreName) {
      // Collapse if already expanded
      trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_GENRE_EXPANDED, { genre_name: genreName, expanded: false })
      setExpandedGenre(null)
    } else {
      trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_GENRE_EXPANDED, { genre_name: genreName, expanded: true })
      // Expand and fetch data if not already loaded
      if (genreData[genreName]) {
        // Data already loaded, just expand
        setExpandedGenre(genreName)
      } else {
        // Fetch data first
        fetchGenreInfo(genreName)
      }
    }
  }

  // Format date from year and month
  const formatDateLabel = (year, month) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const shortYear = year.toString().slice(-2)
    return `${monthNames[month - 1]} ${shortYear}`
  }

  // Prepare chart data
  const prepareChartData = (data) => {
    if (!data || !Array.isArray(data)) return { categories: [], series: [] }

    // Sort data by year and month (oldest first)
    const sortedData = [...data].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year
      return a.month - b.month
    })

    // Take only last 20 months
    const last20Months = sortedData.slice(-20)

    const categories = last20Months.map(item => formatDateLabel(item.year, item.month))
    const series = last20Months.map(item => item.count)

    return { categories, series }
  }

  // Prepare comparison chart data for multiple genres
  const prepareComparisonChartData = (data, selectedGenresList) => {
    if (!data || !Array.isArray(data) || !selectedGenresList || selectedGenresList.length === 0) {
      return { categories: [], series: [] }
    }

    // Group data by genre
    const genreGroups = {}
    selectedGenresList.forEach(genre => {
      genreGroups[genre] = []
    })

    data.forEach(item => {
      if (genreGroups[item.genre]) {
        genreGroups[item.genre].push(item)
      }
    })

    // Get all unique date combinations and sort them
    const allDates = new Set()
    Object.values(genreGroups).forEach(genreData => {
      genreData.forEach(item => {
        const dateKey = `${item.year}-${item.month.toString().padStart(2, '0')}`
        allDates.add(dateKey)
      })
    })

    const sortedDates = Array.from(allDates).sort().slice(-20) // Last 20 months

    // Create categories
    const categories = sortedDates.map(dateKey => {
      const [year, month] = dateKey.split('-')
      return formatDateLabel(parseInt(year), parseInt(month))
    })

    // Create series for each genre
    const series = selectedGenresList.map(genre => {
      const genreDataMap = new Map()
      if (genreGroups[genre]) {
        genreGroups[genre].forEach(item => {
          const dateKey = `${item.year}-${item.month.toString().padStart(2, '0')}`
          genreDataMap.set(dateKey, item.count)
        })
      }

      return {
        name: genre,
        data: sortedDates.map(dateKey => genreDataMap.get(dateKey) || null)
      }
    })

    return { categories, series }
  }

  // Handle checkbox change
  const handleCheckboxChange = (genreName) => {
    setSelectedGenres(prev => {
      if (prev.includes(genreName)) {
        return prev.filter(name => name !== genreName)
      } else {
        return [...prev, genreName]
      }
    })
  }

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedGenres(genres.map(g => g.genre))
    } else {
      setSelectedGenres([])
    }
  }

  // Fetch comparison data
  const fetchComparisonData = async () => {
    if (selectedGenres.length < 2) {
      triggerToast('Please select at least two genres to compare.', 'warning', 'Select Genres')
      return
    }

    try {
      trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_GENRE_COMPARED, { genre_count: selectedGenres.length })
      setLoadingComparison(true)
      const genresParam = selectedGenres.join(',')
      
      // Format dates for API (YYYY-MM:YYYY-MM format)
      const fromMonth = formatDateForAPI(fromDate)
      const toMonth = formatDateForAPI(toDate)
      
      // Build query params
      const params = {
        genres: genresParam
      }
      if (fromMonth && toMonth) {
        params.range = `${fromMonth}:${toMonth}`
      }
      
      const response = await axios.get(`${api}/tracker/stats/genre_info`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      })
      
      if (response.data && response.data.status === 200 && response.data.data) {
        setComparisonData(response.data.data)
        setShowComparisonModal(true)
      }
    } catch (error) {
      console.error("Error fetching comparison data:", error)
      triggerToast('Failed to load comparison data. Please try again.', 'error', 'Error')
    } finally {
      setLoadingComparison(false)
    }
  }

  // Color palette for multiple genres
  const genreColors = [
    '#F6255A', // Pink
    '#34C38F', // Green
    '#5B73E8', // Blue
    '#F1B44C', // Yellow
    '#E83E8C', // Magenta
    '#30ACCF', // Cyan
    '#F46A6A', // Red
    '#556EE6', // Indigo
  ]

  // Commented out selection and comparison logic
  // const handleCheckboxChange = (appId) => {
  //   setSelectedApps(prev => {
  //     if (prev.includes(appId)) {
  //       return prev.filter(id => id !== appId)
  //     } else {
  //       return [...prev, appId]
  //     }
  //   })
  // }

  // const handleSelectAll = (e) => {
  //   if (e.target.checked) {
  //     setSelectedApps(apps.map(app => app.item_id))
  //   } else {
  //     setSelectedApps([])
  //   }
  // }

  // const handleCompare = () => {
  //   if (selectedApps.length > 0) {
  //     setShowComparison(true)
  //     setActiveTab("performance")
  //   }
  // }

  const formatNumber = (num) => {
    if (!num && num !== 0) return "N/A"
    if (num >= 1e6) return (num / 1e6).toFixed(1) + "M"
    if (num >= 1e3) return (num / 1e3).toFixed(1) + "K"
    return num.toString()
  }

  // Validate date range - must be at least 1 month
  const validateDateRange = (from, to) => {
    if (!from || !to) return false
    
    const fromDateObj = new Date(from)
    const toDateObj = new Date(to)
    
    if (toDateObj < fromDateObj) return false
    
    // Calculate difference in months
    const monthsDiff = (toDateObj.getFullYear() - fromDateObj.getFullYear()) * 12 + 
                       (toDateObj.getMonth() - fromDateObj.getMonth())
    
    // If months difference is 0, check if days difference is at least 30
    if (monthsDiff === 0) {
      const daysDiff = Math.floor((toDateObj - fromDateObj) / (1000 * 60 * 60 * 24))
      return daysDiff >= 30
    }
    
    // Must be at least 1 month
    return monthsDiff >= 1
  }

  const handleFromDateChange = (e) => {
    const newFromDate = e.target.value
    setFromDate(newFromDate)
    
    // Validate if toDate is already set
    if (toDate && !validateDateRange(newFromDate, toDate)) {
      triggerToast('Date range must be at least 1 month. Please select a valid range.', 'error', 'Invalid Date Range')
      // Reset to previous valid date
      const prevFromDate = sessionStorage.getItem('topGenres2025_fromDate') || '2025-01-01'
      setFromDate(prevFromDate)
      return
    }
    
    // If valid, update session storage
    try {
      sessionStorage.setItem('topGenres2025_fromDate', newFromDate)
    } catch (e) {}
  }

  const handleToDateChange = (e) => {
    const newToDate = e.target.value
    setToDate(newToDate)
    
    // Validate if fromDate is already set
    if (fromDate && !validateDateRange(fromDate, newToDate)) {
      triggerToast('Date range must be at least 1 month. Please select a valid range.', 'error', 'Invalid Date Range')
      // Reset to previous valid date
      const prevToDate = sessionStorage.getItem('topGenres2025_toDate') || '2025-12-31'
      setToDate(prevToDate)
      return
    }
    
    // If valid, update session storage
    try {
      sessionStorage.setItem('topGenres2025_toDate', newToDate)
    } catch (e) {}
  }

  const handleDateRangeApply = () => {
    if (!validateDateRange(fromDate, toDate)) {
      triggerToast('Date range must be at least 1 month. Please select a valid range.', 'error', 'Invalid Date Range')
      return
    }

    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_DATE_RANGE_APPLIED, { from_date: fromDate, to_date: toDate })

    try {
      sessionStorage.setItem('topGenres2025_fromDate', fromDate)
      sessionStorage.setItem('topGenres2025_toDate', toDate)
    } catch (e) {}
    
    // Clear cached genre data to force refresh with new date range
    setGenreData({})
    setExpandedGenre(null)
    setLoadingGenreData({})
    
    setLoading(true)
    fetchTopGenres()
  }

  return (
    <>
      <SEO
        title={applySEODate(PAGE_SEO.topGenres2025.title)}
        description={PAGE_SEO.topGenres2025.description}
        keywords={PAGE_SEO.topGenres2025.keywords}
        canonical={generateCanonicalUrl("/top-genres-2025")}
        robots={PAGE_SEO.topGenres2025.robots}
      />

      <div className="custom-insight-page-content top-improved-container">
        <div className="top-header-section" style={{ paddingBottom: '8px', marginBottom: 0, paddingLeft: '32px', paddingRight: '32px' }}>
          <div className="title-area">
            <h1 className="top-improved-title" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 0 }}>
              Top Genres of 2025
            </h1>
          </div>
        </div>

        <div className="top-improved-description-section" style={{ marginTop: '4px', paddingTop: 0, paddingLeft: '32px', paddingRight: '32px' }}>
          <p className="top-improved-description" style={{ marginTop: 0, marginBottom: 0 }}>
            Discover the top genres of 2025. View the most popular genres ranked by apps released during the selected period.
          </p>
          
          {/* Date Range Picker */}
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
              <label style={{ fontSize: 12, color: '#A1ACB8', marginBottom: 6 }}>From</label>
              <input
                type="date"
                value={fromDate || ''}
                onChange={handleFromDateChange}
                min="2025-01-01"
                max="2025-12-31"
                style={{ height: 34, borderRadius: 6, padding: '6px 8px', background: '#171821', border: '1px solid rgba(53,55,72,1)', color: '#fff' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
                <label style={{ fontSize: 12, color: '#A1ACB8', marginBottom: 6 }}>To</label>
                <input
                  type="date"
                  value={toDate || ''}
                  onChange={handleToDateChange}
                  min="2025-01-01"
                  max="2025-12-31"
                  style={{ height: 34, borderRadius: 6, padding: '6px 8px', background: '#171821', border: '1px solid rgba(53,55,72,1)', color: '#fff' }}
                />
              </div>

              <button
                type="button"
                onClick={handleDateRangeApply}
                style={{ padding: '8px 16px', borderRadius: 8, background: '#F6255A', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e01e4f'
                  e.target.style.transform = 'translateY(-1px)'
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#F6255A'
                  e.target.style.transform = 'translateY(0)'
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>

        {/* Selection Controls */}
        {!loading && genres.length > 0 && (
          <div className="selection-controls" style={{ 
            marginTop: 24, 
            marginBottom: 24, 
            paddingLeft: '32px', 
            paddingRight: '32px',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: 16 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                cursor: 'pointer', 
                color: '#E8E8E8', 
                fontSize: 14,
                fontWeight: 500
              }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedGenres.length === genres.length && genres.length > 0}
                  onChange={handleSelectAll}
                  style={{ 
                    width: 18, 
                    height: 18, 
                    cursor: 'pointer',
                    accentColor: '#F6255A'
                  }}
                />
                Select All
              </label>
              {selectedGenres.length > 0 && (
                <span style={{ 
                  color: '#A1ACB8', 
                  fontSize: 14,
                  background: 'rgba(246, 37, 90, 0.1)',
                  padding: '4px 12px',
                  borderRadius: 12,
                  border: '1px solid rgba(246, 37, 90, 0.2)'
                }}>
                  {selectedGenres.length} genre{selectedGenres.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
            
            <button
              type="button"
              onClick={fetchComparisonData}
              disabled={selectedGenres.length < 2 || loadingComparison}
              className="compare-button"
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                background: selectedGenres.length >= 2 ? '#F6255A' : '#353748',
                color: '#fff',
                border: 'none',
                cursor: selectedGenres.length >= 2 ? 'pointer' : 'not-allowed',
                fontWeight: 600,
                fontSize: 14,
                transition: 'all 0.2s ease',
                opacity: selectedGenres.length >= 2 ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {loadingComparison ? 'Loading...' : `Compare (${selectedGenres.length})`}
            </button>
          </div>
        )}

        {/* Genres List */}
        <div className="app-cards-container" style={{ paddingLeft: '32px', paddingRight: '32px' }}>
          {loading ? (
            <div className="skeleton-container">
              {[...Array(10)].map((_, index) => (
                <div className="app-card skeleton-card" key={index}>
                  <div className="app-info">
                    <div>
                      <SkeletonParagraph lines={2} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : genres.length === 0 ? (
            <div className="no-data-container">
              <p className="no-data-message">No genres available</p>
            </div>
          ) : (
            <>
              {genres.map((genre, idx) => {
                const genreName = genre.genre || "Unknown Genre"
                const isExpanded = expandedGenre === genreName
                const chartData = genreData[genreName] ? prepareChartData(genreData[genreName]) : { categories: [], series: [] }
                const isLoadingChart = loadingGenreData[genreName]

                const isSelected = selectedGenres.includes(genreName)

                return (
                  <div key={genreName || idx}>
                    <div
                      className="app-card genre-card"
                      style={{
                        border: isSelected ? '1px solid rgba(246, 37, 90, 0.5)' : '1px solid #353748',
                        background: isSelected ? 'rgba(246, 37, 90, 0.06)' : '#21222D',
                        padding: '10px 16px',
                        minHeight: 'auto',
                      }}
                      onClick={(e) => {
                        // Don't trigger expand if clicking on checkbox
                        if (e.target.type === 'checkbox' || e.target.closest('input[type="checkbox"]')) {
                          return
                        }
                        handleGenreClick(genreName)
                      }}
                    >
                      <div className="app-info" style={{ flexDirection: 'row', alignItems: 'center', height: 'auto', gap: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 16 }}>
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation()
                                handleCheckboxChange(genreName)
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: 18,
                                height: 18,
                                cursor: 'pointer',
                                accentColor: '#F6255A',
                                flexShrink: 0
                              }}
                            />
                            <h2 className="app-name" style={{ margin: 0, fontSize: '17px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {genreName}
                            </h2>
                            {isExpanded ? (
                              <ChevronUp size={18} color="#F6255A" style={{ flexShrink: 0 }} />
                            ) : (
                              <ChevronDown size={18} color="#A1ACB8" style={{ flexShrink: 0 }} />
                            )}
                          </div>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 2,
                            padding: '6px 14px',
                            background: 'rgba(255, 255, 255, 0.04)',
                            borderRadius: 10,
                            minWidth: 140,
                            flexShrink: 0,
                          }}>
                            <span style={{
                              color: '#8b93a5',
                              fontSize: 11,
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}>
                              {genre.count === 1 ? 'App Released' : 'Apps Released'}
                            </span>
                            <span style={{
                              color: '#fff',
                              fontSize: 18,
                              fontWeight: 700,
                            }}>
                              {formatNumber(genre.count)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Chart Section */}
                    {isExpanded && (
                      <div style={{
                        marginTop: 16,
                        marginBottom: 24,
                        padding: '24px',
                        background: '#21222D',
                        borderRadius: 12,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        animation: 'fadeInUp 0.3s ease-out'
                      }}>
                        <h3 style={{
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 600,
                          marginBottom: 20,
                          marginTop: 0
                        }}>
                          Apps Released Over Time - {genreName}
                        </h3>
                        
                        {isLoadingChart ? (
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            height: 300,
                            color: '#A1ACB8'
                          }}>
                            Loading chart data...
                          </div>
                        ) : chartData.categories.length > 0 ? (
                          <ReactApexChart
                            options={{
                              chart: {
                                type: 'line',
                                height: 350,
                                toolbar: {
                                  show: false
                                },
                                zoom: {
                                  enabled: false
                                },
                                background: 'transparent',
                                foreColor: '#ffffff'
                              },
                              colors: ['#F6255A'],
                              stroke: {
                                curve: 'straight',
                                width: 3
                              },
                              markers: {
                                size: 5,
                                colors: ['#F6255A'],
                                strokeColors: '#fff',
                                strokeWidth: 2,
                                hover: {
                                  size: 7
                                }
                              },
                              dataLabels: {
                                enabled: false
                              },
                              grid: {
                                borderColor: 'rgba(255, 255, 255, 0.1)',
                                strokeDashArray: 3
                              },
                              xaxis: {
                                categories: chartData.categories,
                                labels: {
                                  style: {
                                    colors: '#ffffff',
                                    fontSize: '11px'
                                  },
                                  rotate: -45,
                                  rotateAlways: false
                                },
                                axisBorder: {
                                  color: 'rgba(255, 255, 255, 0.1)'
                                }
                              },
                              yaxis: {
                                title: {
                                  text: 'Apps Released',
                                  style: {
                                    color: '#ffffff',
                                    fontSize: '12px',
                                    fontWeight: 600
                                  }
                                },
                                labels: {
                                  style: {
                                    colors: '#ffffff',
                                    fontSize: '11px'
                                  }
                                },
                                axisBorder: {
                                  color: 'rgba(255, 255, 255, 0.1)'
                                }
                              },
                              tooltip: {
                                theme: 'dark',
                                y: {
                                  formatter: function (val) {
                                    return val + " apps"
                                  }
                                }
                              },
                              legend: {
                                show: false
                              }
                            }}
                            series={[{
                              name: 'Apps Released',
                              data: chartData.series
                            }]}
                            type="line"
                            height={350}
                          />
                        ) : (
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            height: 300,
                            color: '#A1ACB8'
                          }}>
                            No data available for this genre
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>

        {/* Comparison Modal */}
        <Modal
          isOpen={showComparisonModal}
          toggle={() => setShowComparisonModal(false)}
          centered
          size="lg"
          style={{ maxWidth: '90vw' }}
        >
          <ModalHeader
            toggle={() => setShowComparisonModal(false)}
            style={{
              background: '#21222D',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#fff'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <h4 style={{ margin: 0, color: '#fff', fontSize: 20, fontWeight: 600 }}>
                Genre Comparison
              </h4>
            </div>
          </ModalHeader>
          <ModalBody style={{ background: '#171821', padding: '24px' }}>
            {loadingComparison ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 400,
                color: '#A1ACB8'
              }}>
                Loading comparison data...
              </div>
            ) : comparisonData && selectedGenres.length > 0 ? (
              <>
                <div style={{ marginBottom: 20 }}>
                  <p style={{ color: '#E8E8E8', fontSize: 14, margin: 0 }}>
                    Comparing {selectedGenres.length} genre{selectedGenres.length !== 1 ? 's' : ''}: {selectedGenres.join(', ')}
                  </p>
                </div>
                {(() => {
                  const chartData = prepareComparisonChartData(comparisonData, selectedGenres)
                  return chartData.categories.length > 0 ? (
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'line',
                          height: 400,
                          toolbar: {
                            show: false
                          },
                          zoom: {
                            enabled: true,
                            type: 'x',
                            autoScaleYaxis: true
                          },
                          background: 'transparent',
                          foreColor: '#ffffff',
                          offsetY: 10
                        },
                        colors: selectedGenres.map((_, idx) => genreColors[idx % genreColors.length]),
                        stroke: {
                          curve: 'straight',
                          width: 3
                        },
                        markers: {
                          size: 5,
                          strokeColors: '#fff',
                          strokeWidth: 2,
                          hover: {
                            size: 7
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        grid: {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                          strokeDashArray: 3,
                          padding: {
                            top: 10,
                            right: 10,
                            bottom: 0,
                            left: 0
                          }
                        },
                        xaxis: {
                          categories: chartData.categories,
                          labels: {
                            style: {
                              colors: '#ffffff',
                              fontSize: '11px'
                            },
                            rotate: -45,
                            rotateAlways: false
                          },
                          axisBorder: {
                            color: 'rgba(255, 255, 255, 0.1)'
                          }
                        },
                        yaxis: {
                          title: {
                            text: 'Apps Released',
                            style: {
                              color: '#ffffff',
                              fontSize: '12px',
                              fontWeight: 600
                            }
                          },
                          labels: {
                            style: {
                              colors: '#ffffff',
                              fontSize: '11px'
                            }
                          },
                          axisBorder: {
                            color: 'rgba(255, 255, 255, 0.1)'
                          }
                        },
                        tooltip: {
                          theme: 'dark',
                          y: {
                            formatter: function (val) {
                              return val !== null ? val + " apps" : "No data"
                            }
                          }
                        },
                        legend: {
                          show: true,
                          position: 'top',
                          horizontalAlign: 'right',
                          floating: false,
                          offsetY: -10,
                          offsetX: 0,
                          labels: {
                            colors: '#ffffff',
                            useSeriesColors: false,
                            style: {
                              fontSize: '13px',
                              fontWeight: 500
                            }
                          },
                          markers: {
                            width: 12,
                            height: 12,
                            radius: 6,
                            fillColors: selectedGenres.map((_, idx) => genreColors[idx % genreColors.length]),
                            strokeWidth: 0,
                            shape: 'circle'
                          },
                          itemMargin: {
                            horizontal: 20,
                            vertical: 8
                          },
                          formatter: function(seriesName, opts) {
                            return seriesName
                          },
                          onItemClick: {
                            toggleDataSeries: false
                          },
                          onItemHover: {
                            highlightDataSeries: true
                          }
                        }
                      }}
                      series={chartData.series}
                      type="line"
                      height={400}
                    />
                  ) : (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 400,
                      color: '#A1ACB8'
                    }}>
                      No data available for comparison
                    </div>
                  )
                })()}
              </>
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 400,
                color: '#A1ACB8'
              }}>
                No comparison data available
              </div>
            )}
          </ModalBody>
        </Modal>
      </div>

      <style>{`
        .modal-content {
          background-color: #171821 !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 12px !important;
        }
        
        .modal-header .btn-close {
          filter: invert(1);
          opacity: 0.8;
        }
        
        .modal-header .btn-close:hover {
          opacity: 1;
        }
        
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .comparison-section > div {
          animation: fadeInUp 0.5s ease-out;
        }
        
        .custom-comparison-tabs .nav-link {
          color: rgba(255, 255, 255, 0.6) !important;
          border: none !important;
          border-bottom: 3px solid transparent !important;
          padding: 14px 24px !important;
          background: transparent !important;
          font-weight: 700 !important;
          font-size: 15px !important;
          transition: all 0.3s ease !important;
          position: relative !important;
        }
        .custom-comparison-tabs .nav-link::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #F6255A 0%, #EC4899 100%);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        .custom-comparison-tabs .nav-link:hover {
          color: rgba(255, 255, 255, 0.95) !important;
          background: rgba(246, 37, 90, 0.08) !important;
        }
        .custom-comparison-tabs .nav-link:hover::before {
          transform: scaleX(0.5);
        }
        .custom-comparison-tabs .nav-link.active {
          color: #F6255A !important;
          background: rgba(246, 37, 90, 0.1) !important;
        }
        .custom-comparison-tabs .nav-link.active::before {
          transform: scaleX(1);
        }
        .custom-comparison-tabs .nav-tabs {
          border-bottom: 2px solid rgba(246, 37, 90, 0.2) !important;
          margin-bottom: 0 !important;
        }
        .genre-card {
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .genre-card:hover {
          border-color: rgba(246, 37, 90, 0.4) !important;
        }
        .compare-button:hover:not(:disabled) {
          background: #e01e4f !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(246, 37, 90, 0.4);
        }
        @media (max-width: 992px) {
          .selection-controls,
          .comparison-section {
            padding-left: 48px !important;
            padding-right: 24px !important;
          }
          .comparison-section > div > div {
            grid-template-columns: 150px repeat(auto-fit, minmax(120px, 1fr)) !important;
          }
        }
        @media (max-width: 768px) {
          .selection-controls,
          .comparison-section {
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
          .comparison-section > div > div {
            padding: 24px !important;
          }
          .comparison-section > div > div > div:first-child > div:first-child {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .comparison-section > div > div > div:first-child > div:first-child > div:first-child {
            grid-column: 1 / -1;
          }
        }
      `}</style>
    </>
  )
}
